import { Heart } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";

/**
 * A whimsical heart toggle for saving a book to a reader's personal library.
 *
 * - Filled + rose-pink when favorited.
 * - Outline + soft grey when not.
 * - If the visitor isn't signed in, tapping it invites them to Google-sign-in
 *   instead of doing nothing.
 *
 * `variant`:
 *   "chip"    — small pill for /books grid cards (top-right)
 *   "pill"    — big pill for BookDetail next to the buy button
 */
export default function FavoriteButton({ bookId, variant = "chip", className = "" }) {
  const { user, signInWithGoogle } = useAuth();
  const { isFavorited, toggle, canFavorite } = useFavorites();
  const favorited = isFavorited(bookId);

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canFavorite) {
      if (user === null) return; // still loading auth
      toast("Sign in with Google to build your little library", {
        action: { label: "Sign in", onClick: signInWithGoogle },
      });
      return;
    }
    const res = await toggle(bookId);
    if (res?.needsAuth) return;
    if (res?.error) {
      toast.error("Couldn't save that one — try again in a sec.");
      return;
    }
    toast.success(res.favorited ? "Saved to your library 💗" : "Removed from your library");
  };

  if (variant === "pill") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={favorited}
        aria-label={favorited ? "Remove from my library" : "Save to my library"}
        className={`inline-flex items-center gap-2 px-5 py-3 rounded-full font-fredoka text-sm transition-all ${className}`}
        style={{
          background: favorited ? "#FFE1EC" : "#FFFFFF",
          color: favorited ? "#D6407E" : "#2D3055",
          border: `1.5px solid ${favorited ? "#F6ACC7" : "#E4DFF6"}`,
          boxShadow: favorited
            ? "0 8px 22px rgba(214,64,126,0.22)"
            : "0 6px 14px rgba(45,48,85,0.08)",
        }}
        data-testid="book-favorite-btn"
      >
        <Heart
          size={18}
          fill={favorited ? "#FF6B9D" : "none"}
          stroke={favorited ? "#FF6B9D" : "#7E82A1"}
          strokeWidth={2}
        />
        {favorited ? "In your library" : "Save to library"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={favorited}
      aria-label={favorited ? "Remove from my library" : "Save to my library"}
      className={`w-9 h-9 rounded-full flex items-center justify-center transition-transform hover:scale-110 ${className}`}
      style={{
        background: favorited ? "#FFE1EC" : "rgba(255,255,255,0.94)",
        boxShadow: "0 4px 12px rgba(45,48,85,0.12)",
        border: `1px solid ${favorited ? "#F6ACC7" : "#F0EBFF"}`,
      }}
      data-testid={`book-favorite-btn-${bookId}`}
    >
      <Heart
        size={16}
        fill={favorited ? "#FF6B9D" : "none"}
        stroke={favorited ? "#FF6B9D" : "#7E82A1"}
        strokeWidth={2.2}
      />
    </button>
  );
}
